import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const IDLE_MS = 600_000;       // 10 minutes of inactivity
const WARN_BEFORE_MS = 30_000; // toast 30s before logout

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
  "click",
];

/**
 * InactivityWatcher
 *
 * Mounts inside the AuthProvider tree. While the user is logged in, it
 * resets a 60-second timer on any user activity. After 50s of idle it shows
 * a soft warning toast; at 60s it forces a logout and redirects to /login.
 *
 * The watcher is mounted globally but is a no-op for unauthenticated visitors
 * so it never disturbs the landing page or login flow.
 */
export default function InactivityWatcher() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const idleTimer = useRef(null);
  const warnTimer = useRef(null);
  const loggingOut = useRef(false);

  const clearTimers = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    idleTimer.current = null;
    warnTimer.current = null;
  };

  const doLogout = useCallback(async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    clearTimers();
    try { await logout(); } catch { /* ignore */ }
    toast.message("Signed out for your security", {
      description: "You were inactive for 1 minute.",
      duration: 5000,
    });
    navigate("/login", { replace: true });
    // Allow re-arming if user logs back in
    setTimeout(() => { loggingOut.current = false; }, 1000);
  }, [logout, navigate]);

  const arm = useCallback(() => {
    clearTimers();
    if (!user || user === false || user === null) return;
    warnTimer.current = setTimeout(() => {
      toast.warning("You'll be signed out in 10 seconds", {
        description: "Move your mouse or press a key to stay signed in.",
        duration: 9000,
      });
    }, IDLE_MS - WARN_BEFORE_MS);
    idleTimer.current = setTimeout(doLogout, IDLE_MS);
  }, [user, doLogout]);

  useEffect(() => {
    if (!user || user === false || user === null) {
      clearTimers();
      return undefined;
    }
    const onActivity = () => arm();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const onVis = () => { if (document.visibilityState === "visible") arm(); };
    document.addEventListener("visibilitychange", onVis);
    arm();
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVis);
      clearTimers();
    };
  }, [user, arm]);

  return null;
}
